import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sparkles, ChevronDown, ChevronUp, Eye, Loader2, RefreshCw } from 'lucide-react';
import { useAITalentRecommendations, TalentRecommendation } from '@/hooks/useAITalentRecommendations';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';

interface AITalentRecommendationsProps {
  dealId: string;
  onViewProfile: (profileId: string) => void;
}

const RecommendationCard: React.FC<{
  recommendation: TalentRecommendation;
  onViewProfile: () => void;
  onPairTalent: (profileId: string) => Promise<void>;
  isPaired: boolean;
  isPairing: boolean;
}> = ({ recommendation, onViewProfile, onPairTalent, isPaired, isPairing }) => {
  const [isOpen, setIsOpen] = useState(false);

  const getMatchScoreColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 75) return 'bg-blue-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  const getMatchScoreBadgeVariant = (score: number): "default" | "secondary" | "destructive" | "outline" => {
    if (score >= 90) return 'default';
    if (score >= 75) return 'secondary';
    return 'outline';
  };

  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  return (
    <Card className="relative overflow-hidden border-2 border-primary/20 hover:border-primary/40 transition-all">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
      
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <Avatar className="h-16 w-16">
            <AvatarImage src={recommendation.avatar_url || undefined} alt={recommendation.name} />
            <AvatarFallback className="text-lg">
              {recommendation.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </AvatarFallback>
          </Avatar>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="font-semibold text-lg leading-tight">{recommendation.name}</h3>
                <p className="text-sm text-muted-foreground">{recommendation.sport}</p>
              </div>
              
              {/* Match Score Badge */}
              <Badge 
                variant={getMatchScoreBadgeVariant(recommendation.match_score)}
                className="shrink-0"
              >
                <div className={`w-2 h-2 rounded-full ${getMatchScoreColor(recommendation.match_score)} mr-1.5`} />
                {recommendation.match_score}% Match
              </Badge>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-2 mb-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Badge variant="outline" className="font-normal">
                  {formatFollowers(recommendation.total_followers)} followers
                </Badge>
              </span>
              <Badge variant="outline" className="font-normal">
                {recommendation.tier} Tier
              </Badge>
              {recommendation.competition_level && (
                <Badge variant="outline" className="font-normal">
                  {recommendation.competition_level}
                </Badge>
              )}
              {recommendation.team_name && (
                <Badge variant="outline" className="font-normal">
                  {recommendation.team_name}
                </Badge>
              )}
            </div>

            {/* Key Strengths */}
            <div className="mb-3">
              <div className="flex flex-wrap gap-1.5">
                {recommendation.key_strengths.map((strength, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {strength}
                  </Badge>
                ))}
              </div>
            </div>

            {/* AI Reasoning - Collapsible */}
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between h-8 mb-2">
                  <span className="text-xs font-medium">Why this match?</span>
                  {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-3 bg-muted/50 rounded-md text-sm text-muted-foreground mb-3">
                  {recommendation.reasoning}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onViewProfile}
                className="flex-1"
              >
                <Eye className="h-4 w-4 mr-1.5" />
                View Profile
              </Button>
              
              <Button 
                variant={isPaired ? "secondary" : "default"}
                size="sm" 
                onClick={() => !isPaired && onPairTalent(recommendation.profile_id)}
                disabled={isPaired || isPairing}
                className="flex-1"
              >
                {isPairing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Pairing...
                  </>
                ) : isPaired ? (
                  'Pending Approval'
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1.5" />
                    Pair with Talent
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2, 3].map(i => (
      <Card key={i} className="border-2 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="flex-1 space-y-3">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-6 w-24" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-24" />
              </div>
              <Skeleton className="h-8 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

export const AITalentRecommendations: React.FC<AITalentRecommendationsProps> = ({
  dealId,
  onViewProfile
}) => {
  const { recommendations, loading, error, fetchRecommendations, refreshRecommendations, pairTalentWithDeal } = 
    useAITalentRecommendations(dealId);
  const [pairedTalentIds, setPairedTalentIds] = useState<Set<string>>(new Set());
  const [pairingTalentId, setPairingTalentId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const handlePairTalent = async (talentProfileId: string) => {
    setPairingTalentId(talentProfileId);
    
    try {
      await pairTalentWithDeal(talentProfileId);
      
      setPairedTalentIds(prev => new Set([...prev, talentProfileId]));
      
      toast({
        title: "Talent Paired Successfully",
        description: "The talent has been paired with this deal and will review the opportunity.",
      });
    } catch (error: any) {
      toast({
        title: "Pairing Failed",
        description: error.message || "Failed to pair talent with deal",
        variant: "destructive"
      });
    } finally {
      setPairingTalentId(null);
    }
  };

  if (error && !loading && recommendations.length === 0) {
    return (
      <Card className="mt-8 border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>AI-Recommended Talents</CardTitle>
          </div>
          <CardDescription>
            Could not load recommendations at this time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" size="sm" onClick={refreshRecommendations}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading && recommendations.length === 0) {
    return (
      <Card className="mt-8 border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary animate-pulse" />
                <CardTitle>AI-Recommended Talents</CardTitle>
              </div>
              <CardDescription>
                Analyzing talent pool for best matches...
              </CardDescription>
            </div>
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton />
        </CardContent>
      </Card>
    );
  }

  if (recommendations.length === 0 && !loading) {
    return (
      <Card className="mt-8 border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>AI-Recommended Talents</CardTitle>
          </div>
          <CardDescription>
            Based on your deal requirements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground mb-2">No additional recommendations available</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your eligibility criteria or check back later for new talent matches
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-8 border-2 border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <CardTitle>AI-Recommended Talents</CardTitle>
            </div>
            <CardDescription>
              Top {recommendations.length} matches from our talent pool based on your deal requirements
            </CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={refreshRecommendations}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {recommendations.map((rec) => (
            <RecommendationCard
              key={rec.profile_id}
              recommendation={rec}
              onViewProfile={() => onViewProfile(rec.profile_id)}
              onPairTalent={handlePairTalent}
              isPaired={pairedTalentIds.has(rec.profile_id)}
              isPairing={pairingTalentId === rec.profile_id}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
